import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { auth } from '../firebase';
import '../styles/PrivacyPolicy.css';

function PrivacyPolicy() {
  const location = useLocation();
  const dataDeletionSectionRef = useRef(null);
  
  // Scroll to data deletion section if on the data-deletion route
  useEffect(() => {
    if (location.pathname === '/data-deletion' && dataDeletionSectionRef.current) {
      setTimeout(() => {
        dataDeletionSectionRef.current.scrollIntoView({ 
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [location.pathname, dataDeletionSectionRef]);
  
  return (
    <div className="privacy-policy-container">
      <h1>Privacy Policy</h1>
      <p className="last-updated">Last Updated: {new Date().toLocaleDateString()}</p>
      
      <section>
        <h2>1. Introduction</h2>
        <p>
          Welcome to Sunny Sideup Storys ("we," "our," or "us"). We are committed to protecting your privacy and personal data. 
          This Privacy Policy explains how we collect, use, and share information about you when you use our website, mobile application, 
          and related services (collectively, the "Services").
        </p>
      </section>
      
      <section>
        <h2>2. Information We Collect</h2>
        <p>We collect the following types of information:</p>
        <h3>2.1 Information You Provide</h3>
        <ul>
          <li>Account information: When you create an account, we collect your email address and password</li>
          <li>Profile information: Your name, profile picture, and preferences</li>
          <li>Content: Any information you provide when using our services, including messages and feedback</li>
        </ul>
        
        <h3>2.2 Automatically Collected Information</h3>
        <ul>
          <li>Usage data: Information about how you use our Services, including reading patterns and preferences</li>
          <li>Device information: Device type, operating system, browser type, IP address, and unique device identifiers</li>
          <li>Cookies and similar technologies: We use cookies and similar technologies to collect information about your browsing activities</li>
        </ul>
      </section>
      
      <section>
        <h2>3. How We Use Your Information</h2>
        <p>We use your information for the following purposes:</p>
        <ul>
          <li>To provide and improve our Services</li>
          <li>To personalize your experience</li>
          <li>To communicate with you about updates, promotions, and new features</li>
          <li>To analyze user behavior and optimize our Services</li>
          <li>To protect the security and integrity of our Services</li>
          <li>To comply with legal obligations</li>
        </ul>
      </section>
      
      <section>
        <h2>4. Data Minimization Policy</h2>
        <p>
          We are committed to collecting and processing only the data that is necessary for the purposes specified in this Privacy Policy. 
          Our data minimization practices include:
        </p>
        <ul>
          <li>Collecting only the information that is directly relevant to providing our Services</li>
          <li>Limiting access to personal data to only those employees who require it to perform their job functions</li>
          <li>Regularly reviewing our data collection practices to ensure we are not collecting excessive information</li>
          <li>Implementing technical measures to ensure data is stored only for the necessary time period</li>
          <li>Deleting or anonymizing personal data when it is no longer needed for the purposes for which it was collected</li>
        </ul>
      </section>
      
      <section>
        <h2>5. How We Share Your Information</h2>
        <p>We may share your information with:</p>
        <ul>
          <li>Service providers: Third-party vendors who help us provide our Services</li>
          <li>Business partners: Companies we partner with to offer joint products or services</li>
          <li>Legal authorities: When required by law or to protect our rights</li>
        </ul>
        <p>We do not sell your personal information to third parties.</p>
      </section>
      
      <section>
        <h2>6. Response to Legal Requests</h2>
        <h3>6.1 Required Review of Legal Requests</h3>
        <p>
          When we receive a legal request for user data, we implement the following review process:
        </p>
        <ul>
          <li>All legal requests for user data undergo a thorough review by our legal team</li>
          <li>We verify the authenticity and legality of each request, including the authority of the requesting party</li>
          <li>We ensure that the scope of the request is appropriate and not overly broad</li>
          <li>We evaluate whether the request complies with applicable laws and regulations</li>
          <li>We only provide data in response to valid, legally-binding requests</li>
        </ul>
        
        <h3>6.2 Challenging Unlawful Requests</h3>
        <p>
          We reserve the right to challenge requests for user data that we believe are unlawful, overbroad, or otherwise inappropriate. 
          Our approach to these situations includes:
        </p>
        <ul>
          <li>Pushing back on requests that appear to exceed the requesting party's legal authority</li>
          <li>Seeking clarification or narrowing of overly broad requests</li>
          <li>Notifying affected users about requests for their data when legally permitted to do so</li>
          <li>Using legal means to challenge requests when appropriate, including seeking judicial review</li>
          <li>Only providing the minimum data required to comply with the specific legal request</li>
        </ul>
        
        <h3>6.3 Documentation of Requests</h3>
        <p>
          We maintain comprehensive documentation of all legal requests for user data, including:
        </p>
        <ul>
          <li>The date and nature of each request</li>
          <li>The identity of the requesting party</li>
          <li>The legal basis cited for the request</li>
          <li>Our response to the request, including what data was provided (if any)</li>
          <li>Records of any challenges we made to the request</li>
          <li>These records are maintained securely and reviewed periodically to ensure compliance with our policies</li>
        </ul>
      </section>
      
      <section>
        <h2>7. Your Rights and Choices</h2>
        <p>Depending on your location, you may have the following rights:</p>
        <ul>
          <li>Access and portability: Request a copy of your personal data</li>
          <li>Correction: Update or correct inaccurate data</li>
          <li>Deletion: Request deletion of your personal data</li>
          <li>Objection: Object to certain uses of your data</li>
          <li>Withdrawal of consent: Withdraw consent for processing where applicable</li>
        </ul>
      </section>
      
      <section>
        <h2>8. Data Retention and Security</h2>
        <p>
          We retain your information for as long as necessary to provide our Services, comply with legal obligations, 
          or protect our interests. We implement appropriate technical and organizational measures to protect your personal data.
        </p>
      </section>
      
      <section>
        <h2>9. Children's Privacy</h2>
        <p>
          Our Services are not directed to children under 18. We do not knowingly collect personal information from children.
          If you believe we have collected information from a child, please contact us immediately.
        </p>
      </section>
      
      <section>
        <h2>10. International Data Transfers</h2>
        <p>
          Your information may be transferred to and processed in countries other than your country of residence.
          We ensure appropriate safeguards are in place to protect your information when transferred internationally.
        </p>
      </section>
      
      <section ref={dataDeletionSectionRef}>
        <h2>11. Data Deletion Requests</h2>
        <p>
          You have the right to request the deletion of your personal data. To submit a data deletion request, 
          please email us directly at <a href="mailto:sunnysideupstorys@gmail.com" className="email-link">sunnysideupstorys@gmail.com</a> with the subject line "Data Deletion Request".
        </p>
        
        <div className="deletion-request-guidelines">
          <h3>Data Deletion Process</h3>
          <p>When requesting data deletion, please include the following information:</p>
          <ul>
            <li>The email address associated with your account</li>
            <li>Your full name</li>
            <li>A statement confirming your request to delete your data</li>
          </ul>
          
          <p className="process-note">
            After receiving your request, we will:
          </p>
          <ol>
            <li>Verify your identity to ensure the security of your data</li>
            <li>Process your deletion request within 30 days</li>
            <li>Send you confirmation once your data has been deleted</li>
          </ol>
          
          <p className="contact-note">
            If you have any questions about the data deletion process, please contact us at 
            <a href="mailto:sunnysideupstorys@gmail.com" className="email-link"> sunnysideupstorys@gmail.com</a>.
          </p>
        </div>
      </section>
      
      <section>
        <h2>12. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. We will notify you of any changes by posting the 
          new Privacy Policy on this page and updating the "Last Updated" date.
        </p>
      </section>
      
      <section>
        <h2>13. Contact Us</h2>
        <p>
          If you have any questions about this Privacy Policy, please contact us at:
        </p>
        <div className="contact-info">
          <p>Email: privacy@sunnysideupstorys.com</p>
          <p>Address: Sunny Sideup Storys LLC, 123 Fiction Street, Storyland, ST 12345</p>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPolicy; 